import React, { createContext, useReducer, useEffect, ReactNode } from 'react';
import { useSocket } from '../hooks';
import { logger } from '../utils/logger';
import { 
  subscribeToNotifications, 
  markNotificationAsRead as markNotificationAsReadService, 
  markAllNotificationsAsRead as markAllNotificationsAsReadService,
  clearAllNotifications,
  getNotifications
} from '../services/socket/notificationService';

// Notification type
export interface Notification {
  id: string;
  type: 'meeting' | 'connection' | 'system';
  title: string;
  message: string;
  read: boolean;
  createdAt: Date;
  data?: Record<string, any>;
}

// State type
interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
}

// Initial state
const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
};

// Action types
type NotificationAction =
  | { type: 'FETCH_NOTIFICATIONS_START' }
  | { type: 'FETCH_NOTIFICATIONS_SUCCESS'; payload: Notification[] }
  | { type: 'FETCH_NOTIFICATIONS_FAILURE'; payload: string }
  | { type: 'ADD_NOTIFICATION'; payload: Notification }
  | { type: 'MARK_AS_READ'; payload: string }
  | { type: 'MARK_ALL_AS_READ' }
  | { type: 'CLEAR_NOTIFICATIONS' }
  | { type: 'CLEAR_ERROR' };

// Reducer
const notificationReducer = (
  state: NotificationState,
  action: NotificationAction
): NotificationState => {
  switch (action.type) {
    case 'FETCH_NOTIFICATIONS_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'FETCH_NOTIFICATIONS_SUCCESS':
      return {
        ...state,
        notifications: action.payload,
        unreadCount: action.payload.filter(note => !note.read).length,
        isLoading: false,
        error: null,
      };
    case 'FETCH_NOTIFICATIONS_FAILURE':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [action.payload, ...state.notifications],
        unreadCount: state.unreadCount + (action.payload.read ? 0 : 1),
      };
    case 'MARK_AS_READ':
      return {
        ...state,
        notifications: state.notifications.map(note =>
          note.id === action.payload ? { ...note, read: true } : note
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    case 'MARK_ALL_AS_READ':
      return {
        ...state,
        notifications: state.notifications.map(note => ({ ...note, read: true })),
        unreadCount: 0,
      };
    case 'CLEAR_NOTIFICATIONS':
      return {
        ...state,
        notifications: [],
        unreadCount: 0,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

// Context interface
interface NotificationContextType extends NotificationState {
  fetchNotifications: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  clearNotifications: () => Promise<void>;
  clearError: () => void;
}

// Create context
export const NotificationContext = createContext<NotificationContextType>({
  ...initialState,
  fetchNotifications: async () => {},
  markAsRead: async () => {},
  markAllAsRead: async () => {},
  clearNotifications: async () => {},
  clearError: () => {},
});

// Provider component
interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const { socket, isConnected } = useSocket();

  // Listen for real-time notifications
  useEffect(() => {
    if (socket && isConnected) {
      // Handler for new notifications
      const handleNewNotification = (data: Notification) => {
        dispatch({ type: 'ADD_NOTIFICATION', payload: data });
      };

      // Register socket event listeners
      socket.on('notification', handleNewNotification);
      
      // Listen for notifications response
      socket.on('notifications', (data: { notifications: Notification[] }) => {
        dispatch({ type: 'FETCH_NOTIFICATIONS_SUCCESS', payload: data.notifications });
      });
      
      // Listen for read confirmations
      socket.on('notification:read-confirmed', (data: { id: string }) => {
        // Optimistic update already done, this is just for confirmation
        logger.debug(`Server confirmed notification ${data.id} marked as read`);
      });
      
      // Listen for all-read confirmations
      socket.on('notification:all-read-confirmed', () => {
        // Optimistic update already done, this is just for confirmation
        logger.debug('Server confirmed all notifications marked as read');
      });
      
      // Request initial notifications
      getNotifications(10, 0);

      // Cleanup function
      return () => {
        socket.off('notification', handleNewNotification);
        socket.off('notifications');
        socket.off('notification:read-confirmed');
        socket.off('notification:all-read-confirmed');
      };
    }
    
    // Alternative subscription method if socket not directly available
    const unsubscribe = subscribeToNotifications((notification) => {
      dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
    });
    
    return unsubscribe;
  }, [socket, isConnected]);

  // Fetch notifications
  const fetchNotifications = async (): Promise<void> => {
    try {
      dispatch({ type: 'FETCH_NOTIFICATIONS_START' });
      
      if (socket && isConnected) {
        // Request notifications through socket
        getNotifications(10, 0);
        
        // Socket response is handled in useEffect
      } else {
        // Fallback for development/testing
        await new Promise(resolve => setTimeout(resolve, 500));
        dispatch({ type: 'FETCH_NOTIFICATIONS_SUCCESS', payload: [] });
      }
    } catch (error: any) {
      logger.error('Failed to fetch notifications:', error);
      dispatch({
        type: 'FETCH_NOTIFICATIONS_FAILURE',
        payload: error.message,
      });
    }
  };

  // Mark notification as read
  const markAsRead = async (id: string): Promise<void> => {
    try {
      // Optimistic update
      dispatch({ type: 'MARK_AS_READ', payload: id });
      
      // Send to server
      markNotificationAsReadService(id);
    } catch (error: any) {
      logger.error(`Failed to mark notification ${id} as read:`, error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async (): Promise<void> => {
    try {
      // Optimistic update
      dispatch({ type: 'MARK_ALL_AS_READ' });
      
      // Send to server
      markAllNotificationsAsReadService();
    } catch (error: any) {
      logger.error('Failed to mark all notifications as read:', error);
    }
  };

  // Clear all notifications
  const clearNotifications = async (): Promise<void> => {
    try {
      // Optimistic update
      dispatch({ type: 'CLEAR_NOTIFICATIONS' });
      
      // Send to server
      clearAllNotifications();
    } catch (error: any) {
      logger.error('Failed to clear notifications:', error);
    }
  };

  // Clear error
  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  return (
    <NotificationContext.Provider
      value={{
        ...state,
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        clearNotifications,
        clearError,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
